
import { AxiosResponse } from 'axios';
import { newAppointmentVersionV2Ref } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/NewAppointmentV2/v2';
import { cloneDeep, findLast, first, set, uniqBy } from 'lodash';
import moment from 'moment';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { setLoading } from 'services/UI/sagas';
import { ISagaFunc } from 'services/actionConfigs';
import { IResponseDataBody } from 'services/response';
import shopActions from 'services/shop/actions';
import { ICategoryItemData } from 'services/shop/types/categories';
import { RootState } from 'store';
import storage from 'utils/sessionStorage';
import { DATE_FORMAT } from '../utils/format';
import actions from './actions';
import { default as apis, default as appointmentApis } from './apis';
import { defualtAppointmentColorSets, PATH_LOADING, PROLONGED_TIME_STATUS, saveAppointmentColor } from './constants';
import { aptUIActions } from './reducers';
import { getPureParams, getPureTableParams } from './selectors';
import { IApiGetAppointments, IApiGetSettingBackground, IApiGetTableAppointment, IAppointmentMasterDataParams, IBodyApiUpdateAppointmentWithDrop } from './types/api';
import { IApiGetSummaryByStatus, IApiParamsGetSummaryByStatus, IAppointmentDraftPopupData, IAppointmentItemData, IAppointmentMasterDataResData, ISettingBackgroundItem } from './types/appointment';
import { ILockBreakTimeItem } from './types/lockBreakTime';
import appointmentActions from './actions';
import BookingV4 from '../pages/AppointmentsPage/components/BookingV4';

const getAppointments: ISagaFunc<IApiGetAppointments> = function* ({ payload }) {
  try {
    const resData: AxiosResponse<{ data: IAppointmentItemData[] }> = yield call(apis.getAppointments, payload);
    if (resData?.data?.data) {
      yield put(actions.getAppointments.success(resData.data.data));
    }
  } catch (error) {
    yield put(actions.getAppointments.fail({}));
  } finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const getLockBreakTimes: ISagaFunc<IApiGetAppointments> = function* ({ payload }) {
  try {
    const resData: AxiosResponse<{ data: ILockBreakTimeItem[] }> = yield call(apis.getLockBreakTimes, payload);
    if (resData?.data?.data) {
      yield put(actions.getLockBreakTimes.success(resData.data.data));
    }
  } catch (error) {
    yield put(actions.getLockBreakTimes.fail({}));
  } finally {
  }
};

const getLockBreakTimesForQuickBooking: ISagaFunc<IApiGetAppointments> = function* ({ payload }) {
  try {
    const resData: AxiosResponse<{ data: ILockBreakTimeItem[] }> = yield call(apis.getLockBreakTimes, payload);
    if (resData?.data?.data) {
      yield put(actions.getLockBreakTimesForQuickBooking.success(resData.data.data));
    }
  } catch (error) {
    yield put(actions.getLockBreakTimesForQuickBooking.fail({}));
  }
};

const removeBreakBlockHour: ISagaFunc<string> = function* ({ payload }) {
  const lockBreakTimes: ILockBreakTimeItem[] = yield select((state: RootState) => state.appointment.lockBreakTimes);
  const itemToRevert = lockBreakTimes.find((item) => item.id === payload);

  yield put(aptUIActions.removeBreakBlockHourOptimistic(payload));

  yield setLoading(PATH_LOADING.getLockBreakTimes, true);
  try {
    yield call(apis.removeLockBreakTimes, payload);
  } catch (error) {
    if (itemToRevert) {
      yield put(aptUIActions.revertRemoveBreakBlockHour(itemToRevert));
    }
  } finally {
    yield setLoading(PATH_LOADING.getLockBreakTimes, false);
  }
};

const setParams: ISagaFunc<IApiGetAppointments> = function* ({ payload }) {
  const storeParams: IApiGetAppointments = yield select(getPureParams);
  const params: IApiGetAppointments = {
    ...storeParams ?? {},
    ...payload ?? {}
  };

  yield all([
    put(actions.getAppointments.fetch(params)),
    put(actions.getLockBreakTimes.fetch(params)),
  ]);
};

const initAptList = function* () {
  yield delay(200);
  const storeData: IApiGetAppointments = yield select(getPureParams);
  const today = moment().format(DATE_FORMAT);
  const params = {
    ...storeData,
    startTime: today,
    endTime: today,
  };
  yield put(actions.setParams(params));
  yield put(actions.setTableParams({ ...params, page: 1 }));
};

const addMoreTimeAppointment: ISagaFunc<{ status: PROLONGED_TIME_STATUS, appointmentId: string }> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getAppointments, true);
  try {
    const res: IResponseDataBody<IAppointmentItemData[]> = yield call(appointmentApis.addMoreTimeAppointment, payload);
    if (res.data.data) {
      yield put(aptUIActions.syncAppointments(res.data.data));
      yield put(aptUIActions.clearDraftApt());
    } else {
      yield put(aptUIActions.revertDraftAptFail(payload.appointmentId));
    }
  } catch (error) {
    yield put(aptUIActions.revertDraftAptFail(payload.appointmentId));
  }
  finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const updateAppointmentWithDrop: ISagaFunc<IBodyApiUpdateAppointmentWithDrop> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getAppointments, true);
  yield delay(200);
  const params = { ...payload };
  if (params.distance) {
    delete params.distance;
  }
  try {
    const res: IResponseDataBody<IAppointmentItemData[]> = yield call(appointmentApis.updateAppointmentWithDrop, params);
    if (res.data.data) {
      yield put(aptUIActions.syncAppointments(res.data.data));
      yield put(aptUIActions.clearDraftApt());
    } else {
      yield put(aptUIActions.revertDraftAptFail(payload.appointmentId));
    }
  } catch (error) {
    yield put(aptUIActions.revertDraftAptFail(payload.appointmentId));
  }
  finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
    yield put(aptUIActions.setEnableDrag(false));
  }
};

const addMoreBreakTime: ISagaFunc<string> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getAppointments, true);
  yield delay(200);
  try {
    const res: IResponseDataBody<boolean> = yield call(appointmentApis.addMoreTimeClockTime, payload);
    if (res.data.data) {
      const storeParams = yield select(getPureParams);
      yield put(actions.getLockBreakTimes.fetch(storeParams));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const minusBreakTime: ISagaFunc<string> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getAppointments, true);
  yield delay(200);
  try {
    const res: IResponseDataBody<boolean> = yield call(appointmentApis.minusMoreTimeClockTime, payload);
    if (res.data.data) {
      const storeParams = yield select(getPureParams);
      yield put(actions.getLockBreakTimes.fetch(storeParams));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const setTableParams: ISagaFunc<IApiGetTableAppointment | undefined> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getTableAppointments, true);
  yield delay(200);
  const storeParams = yield select(getPureTableParams);

  const params = {
    ...storeParams ?? {},
    ...payload ?? {}
  };
  if (params.staffId === undefined) yield delete params.staffId;

  yield put(actions.getTableAppointments.fetch(params));
};

const getTableAppointments: ISagaFunc<IApiGetTableAppointment> = function* ({ payload }) {
  yield delay(200);
  try {
    const resData: IResponseDataBody<{ content: IAppointmentItemData[] }> = yield call(apis.getTableAppointments, payload);
    if (resData?.data?.data) {
      yield put(actions.getTableAppointments.success(resData.data.data));
    }
  } catch (error) {
    yield put(actions.getTableAppointments.fail({}));
  } finally {
    yield setLoading(PATH_LOADING.getTableAppointments, false);
  }
};

const loadingView = function* () {
  yield setLoading(PATH_LOADING.getAppointments, true);
  yield delay(400);
  yield setLoading(PATH_LOADING.getAppointments, false);
};

const getListStaffAvailableBooking: ISagaFunc<string> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getStaffs, true);
  try {
    const res = yield call(apis.getListStaffAvailableBooking, payload);
    if (res.data.data) {
      yield put(actions.getListStaffAvailableBooking.success(res.data.data));
    }
  } catch (error) {
  } finally {
    yield setLoading(PATH_LOADING.getStaffs, false);
  }
};

const getListStaffsOff: ISagaFunc<IApiGetAppointments> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.listStaffsOffLoading, true);
  try {
    const res = yield call(apis.getListStaffsOff, payload.startTime, payload.endTime);
    if (res.data.data) {
      yield put(actions.getListStaffsOff.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.listStaffsOffLoading, false);
  }
};

const getServicesByStaff: ISagaFunc<string> = function* ({ payload: staffId }) {
  try {
    const resData: IResponseDataBody<ICategoryItemData[]> = yield call(
      apis.getCategoriesByStaff, staffId
    );
    if (resData?.data?.data) {
      yield put(actions.getServicesByStaff.success(resData?.data?.data));
    }
  } catch (error) {
    yield put(actions.getServicesByStaff.fail({}));
  }
};
const getServicesByStaffQuickBook: ISagaFunc<string> = function* ({ payload: staffId }) {
  try {
    const resData: IResponseDataBody<ICategoryItemData[]> = yield call(
      apis.getCategoriesByStaff, staffId
    );
    if (resData?.data?.data) {
      yield put(actions.getServicesByStaffQuickBook.success({
        id: staffId,
        loading: false,
        categories: resData?.data?.data || [],
      }));
    }
  } catch (error) {
    yield put(actions.getServicesByStaffQuickBook.fail({
      id: staffId,
      loading: false,
      categories: [],
    }));
  }
};

const addAnybody = function* () {
  yield setLoading(PATH_LOADING.getAppointments, true);
  try {
    const resData: IResponseDataBody<boolean> = yield call(apis.addAnybody);
    if (resData?.data?.data) {
      const params: IAppointmentMasterDataParams = yield select((state: RootState) => state.appointment.paramsMasterData);
      yield put(appointmentActions.initData(params.date));
    }
  } catch (error) {
  } finally {
  }
};

const removeAnybody: ISagaFunc<string> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getAppointments, true);
  try {
    const resData: IResponseDataBody<boolean> = yield call(apis.removeAnybody, payload);
    if (resData?.data?.data) {
      const params: IAppointmentMasterDataParams = yield select((state: RootState) => state.appointment.paramsMasterData);
      yield put(appointmentActions.initData(params.date));
    }
  } catch (error) { } finally {
  }
};

const syncSignalBackground = function ({ payload }: { payload: ISettingBackgroundItem[] }) {
  const uniquePayload = uniqBy(payload, 'status');
  storage.appointment_settingBackgrounds.set(JSON.stringify(uniquePayload.map(o => ({ id: o.id, status: o.status?.toString(), color: o.color }))));
  uniquePayload.forEach(item => {
    const [stroke, fill] = item.color?.split('_') || [];
    saveAppointmentColor(item?.status || '', fill, stroke);
  });
};

const getSettingBackground = function* () {
  const _settingBackgrounds: ISettingBackgroundItem[] = yield select((state: RootState) => state.appointment.settingBackgrounds);
  const settingBackgrounds = _settingBackgrounds.length ? _settingBackgrounds : defualtAppointmentColorSets;
  try {
    const res: IResponseDataBody<ISettingBackgroundItem[]> = yield call(apis.getSettingBackground);
    if (res.data.data) {
      const uniquePayload = uniqBy(settingBackgrounds, 'status');
      const result: ISettingBackgroundItem[] = [];
      uniquePayload.map((o) => {
        const item = findLast(res.data.data, (item: ISettingBackgroundItem) => item.status?.toString() === o.status?.toString());
        if (item) {
          result.push({
            ...o,
            id: item.id,
            color: item.color,
          });
        }
      });
      yield put(actions.getSettingBackground.success(result));
    }
  } catch (error) { }
};

const updateSettingBackground = function* ({ payload }: { payload: IApiGetSettingBackground }) {
  const settingBackgrounds: ISettingBackgroundItem[] = yield select((state: RootState) => state.appointment.settingBackgrounds);
  const item = findLast(settingBackgrounds, (item: ISettingBackgroundItem) => item.status?.toString() === payload.status?.toString());
  const [stroke, fill] = payload.color?.split('_') || [];
  yield call(saveAppointmentColor, payload?.status || '', fill, stroke);
  try {
    const res: IResponseDataBody<ISettingBackgroundItem> = yield call(apis.updateSettingBackground, {
      color: item?.color,
      status: item?.status,
      ...payload,
    });
    if (res.data.data) {
      yield put(actions.updateSettingBackground.success(res.data.data));
    }
  } catch (error) { }
};

const updateSettingBackgroundSuccess = function* ({ payload }: { payload: ISettingBackgroundItem }) {
  const [stroke, fill] = payload.color?.split('_') || [];
  yield call(saveAppointmentColor, payload?.status || '', fill, stroke);
  const settingBackgrounds: ISettingBackgroundItem[] = yield select((state: RootState) => state.appointment.settingBackgrounds);
  const values = settingBackgrounds.map(item => {
    const result = ({ id: item.id, status: item.status, color: item.color });
    if (item.status === payload.status) {
      result.color = payload.color;
    }
    return result;
  });

  yield put(actions.getSettingBackground.success(values));
};

const setNewAppointmentDraftData: ISagaFunc<IAppointmentDraftPopupData | null> = function* ({ payload }) {
  if (!payload) return newAppointmentVersionV2Ref.current?.close();
  yield newAppointmentVersionV2Ref.current?.open(payload);
};
const autoScroll: ISagaFunc<IAppointmentItemData[]> = function* ({ payload }) {
  yield delay(200);
  const element = document.getElementById(first(payload)?.appointmentId || '');
  element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
};
const firstSyncBackgroundColors = function* () {
  const _settingBackgrounds: ISettingBackgroundItem[] = storage.appointment_settingBackgrounds.get() ? JSON.parse(storage.appointment_settingBackgrounds.get()) : [];
  const settingBackgrounds = _settingBackgrounds.length ? _settingBackgrounds : defualtAppointmentColorSets;
  yield put(actions.getSettingBackground.success(settingBackgrounds));
};


const initData: ISagaFunc<string> = function* ({ payload }) {
  yield delay(200);
  const today = payload || moment().format(DATE_FORMAT);
  const params = {
    date: today,
    page: 1,
    size: 10,
  };
  yield firstSyncBackgroundColors();
  yield put(actions.getAppointmentMasterData.fetch(params));
  yield put(actions.summaryByStatus.fetch({ startTime: today, endTime: today }));
  yield delay(200);
  yield BookingV4.fetchStaffs(today);
};

const getAppointmentMasterData = function* ({ payload }: { payload: IAppointmentMasterDataParams }) {
  const storeParams: IAppointmentMasterDataParams = yield select((state: RootState) => state.appointment.paramsMasterData);
  const params: IAppointmentMasterDataParams = {
    ...storeParams,
    ...payload,
  };
  yield setLoading(PATH_LOADING.getAppointments, true);
  try {
    const res: IResponseDataBody<IAppointmentMasterDataResData> = yield call(appointmentApis.getAppointmentMasterData, params);
    if (res.data.data.data) {
      yield put(actions.getAppointmentMasterData.success(res.data.data));
      yield put(actions.getAppointmentMasterDataInitSuccess({ date: params.date || '', total: res.data.data.total }));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const getAppointmentMasterDataInitSuccess = function* ({ payload }: { payload: { date: string, total: number } }) {
  const staffSize: number = yield select((state: RootState) => state.appointment.staffSize);
  yield setLoading(PATH_LOADING.getAppointments, false);

  if (payload.total > staffSize) {
    yield put(aptUIActions.setStaffSize(payload.total));
    yield put(actions.getAppointmentMasterData.fetchPaging({ date: payload.date, page: 1, size: payload.total }));
  }
};

const getAppointmentMasterDataFetchPaging = function* ({ payload }: { payload: IAppointmentMasterDataParams }) {
  const storeParams: IAppointmentMasterDataParams = yield select((state: RootState) => state.appointment.paramsMasterData);
  const params: IAppointmentMasterDataParams = {
    ...storeParams,
    ...payload,
  };
  try {
    const res: IResponseDataBody<IAppointmentMasterDataResData> = yield call(appointmentApis.getAppointmentMasterData, params);
    if (res.data.data.data) {
      yield put(actions.getAppointmentMasterData.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const summaryByStatus = function* ({ payload }: { payload: IApiParamsGetSummaryByStatus }) {
  try {
    const res: IResponseDataBody<IApiGetSummaryByStatus[]> = yield call(appointmentApis.getSummaryByStatus, payload);
    if (res.data.data) {
      yield put(actions.summaryByStatus.success(res.data.data));
    }
  } catch (error) {
  } finally {
    yield setLoading(PATH_LOADING.getAppointments, false);
  }
};

const updateColorAppointment = function* ({ payload }: { payload: { appointmentId: string; color: string } }) {
  const currentAppointmentList: IAppointmentItemData[] = yield select((state: RootState) => state.appointment.appointmentList);
  const revertItem = findLast(currentAppointmentList, (item: IAppointmentItemData) => item.appointmentId === payload.appointmentId);
  try {
    const res: IResponseDataBody<IAppointmentItemData> = yield call(appointmentApis.updateColorAppointment, payload);
    if (res.data.data) {
      set(res.data.data, 'action', 'UPDATE');
      yield put(aptUIActions.syncAppointments([res.data.data]));
    }
  } catch (error) {
    if (revertItem) {
      const _revertItem = cloneDeep(revertItem);
      set(_revertItem, 'action', 'UPDATE');
      yield put(aptUIActions.syncAppointments([_revertItem]));
    }
  }
};

export default function* appointmentServiceSagas() {
  yield all([
    takeLatest(actions.updateSettingBackground.fetch, updateSettingBackground),
    takeLatest(actions.updateSettingBackground.success, updateSettingBackgroundSuccess),
    takeLatest(actions.getSettingBackground.fetch, getSettingBackground),
    takeLatest(actions.getSettingBackground.success, syncSignalBackground),
    takeLatest(actions.getAppointments.fetch, getAppointments),
    takeLatest(actions.getLockBreakTimes.fetch, getLockBreakTimes),
    takeLatest(actions.getLockBreakTimesForQuickBooking.fetch, getLockBreakTimesForQuickBooking),
    takeLatest(actions.removeBreakBlockHour, removeBreakBlockHour),
    takeLatest(actions.setParams, setParams),
    takeLatest(actions.initData, initData),
    takeLatest(actions.initAptList, initAptList),
    takeLatest(actions.addMoreTimeAppointment, addMoreTimeAppointment),
    takeLatest(actions.updateAppointmentWithDrop, updateAppointmentWithDrop),
    takeLatest(actions.addMoreBreakTime, addMoreBreakTime),
    takeLatest(actions.minusBreakTime, minusBreakTime),
    takeLatest(actions.setTableParams, setTableParams),
    takeLatest(actions.getTableAppointments.fetch, getTableAppointments),
    takeLatest(actions.setStaffLimit, loadingView),
    takeLatest(actions.decreaseStaffPage, loadingView),
    takeLatest(actions.increaseStaffPage, loadingView),
    takeLatest(actions.getListStaffAvailableBooking.fetch, getListStaffAvailableBooking),
    takeLatest(actions.getListStaffsOff.fetch, getListStaffsOff),
    takeLatest(actions.getServicesByStaff.fetch, getServicesByStaff),
    takeLatest(actions.getServicesByStaffQuickBook.fetch, getServicesByStaffQuickBook),
    takeLatest(actions.addAnybody, addAnybody),
    takeLatest(actions.removeAnybody, removeAnybody),
    takeLatest(actions.setNewAppointmentDraftData, setNewAppointmentDraftData),
    takeLatest(aptUIActions.syncAppointments, autoScroll),
    takeLatest(actions.getAppointmentMasterData.fetch, getAppointmentMasterData),
    takeLatest(actions.getAppointmentMasterData.fetchPaging, getAppointmentMasterDataFetchPaging),
    takeLatest(actions.getAppointmentMasterDataInitSuccess, getAppointmentMasterDataInitSuccess),
    takeLatest(actions.summaryByStatus.fetch, summaryByStatus),
    takeLatest(actions.updateColorAppointment, updateColorAppointment),
  ]);
}
